type OnboardingScreen = 'onboarding-welcome' | 'onboarding-add-pc';

export interface OnboardingGateInputs {
  needsWelcome: boolean;
  needsWelcomeNoTier: boolean;
  needsAttestation: boolean;
}

export type OnboardingResult = OnboardingScreen | 'hub';

type Listener = (screen: OnboardingResult) => void;

const LOG_PREFIX = '[OnboardingFlow]';

/**
 * Evaluates onboarding gates, queues the applicable screens in order
 * (welcome → add-pc), and advances through them one at a time.
 * When the queue is exhausted the result is 'hub'.
 */
export class OnboardingFlowManager {
  private queue: OnboardingScreen[] = [];
  private currentIndex = 0;
  private evaluated = false;
  private listeners: Listener[] = [];

  evaluate(inputs: OnboardingGateInputs): void {
    if (this.evaluated) {
      console.warn(LOG_PREFIX, 'Already evaluated — ignoring duplicate call');
      return;
    }

    console.log(LOG_PREFIX, 'Evaluating onboarding gates:', {
      needsWelcome: inputs.needsWelcome,
      needsWelcomeNoTier: inputs.needsWelcomeNoTier,
      needsAttestation: inputs.needsAttestation,
    });

    this.queue = [];
    this.currentIndex = 0;

    if (inputs.needsWelcome || inputs.needsWelcomeNoTier) {
      this.queue.push('onboarding-welcome');
      const reason = inputs.needsWelcome ? 'user changed' : 'machine has no tier';
      console.log(LOG_PREFIX, `  ✓ Welcome gate PASSED (${reason}) — queued onboarding-welcome`);
    } else {
      console.log(LOG_PREFIX, '  ✗ Welcome gate SKIPPED — user has already seen welcome');
    }

    if (inputs.needsAttestation) {
      this.queue.push('onboarding-add-pc');
      console.log(LOG_PREFIX, '  ✓ Attestation gate PASSED — queued onboarding-add-pc');
    } else {
      console.log(LOG_PREFIX, '  ✗ Attestation gate SKIPPED — user is already attested');
    }

    this.evaluated = true;
    const screen = this.getCurrentScreen();
    console.log(LOG_PREFIX, 'Initial screen:', screen, `(${this.queue.length} onboarding step(s) queued)`);
    this.notify();
  }

  advance(): void {
    if (!this.evaluated) {
      console.warn(LOG_PREFIX, 'advance() called before evaluate()');
      return;
    }
    const prev = this.getCurrentScreen();
    this.currentIndex++;
    const next = this.getCurrentScreen();
    console.log(LOG_PREFIX, `Advancing: ${prev} → ${next}`);
    this.notify();
  }

  /**
   * Clear the one-shot evaluation so the gates can be re-evaluated from
   * scratch. Used when the signed-in user changes mid-session: the stale
   * attestation status from the previous user must not pin the queue.
   * A fresh evaluate() must follow before getCurrentScreen() is meaningful.
   */
  reset(): void {
    this.queue = [];
    this.currentIndex = 0;
    this.evaluated = false;
    console.log(LOG_PREFIX, 'Reset — gates will be re-evaluated on the next evaluate()');
  }

  getCurrentScreen(): OnboardingResult {
    if (!this.evaluated) return 'hub';
    if (this.currentIndex >= this.queue.length) return 'hub';
    return this.queue[this.currentIndex];
  }

  isEvaluated(): boolean {
    return this.evaluated;
  }

  subscribe(listener: Listener): () => void {
    this.listeners.push(listener);
    return () => {
      const idx = this.listeners.indexOf(listener);
      if (idx > -1) this.listeners.splice(idx, 1);
    };
  }

  private notify(): void {
    const screen = this.getCurrentScreen();
    for (const listener of this.listeners) {
      listener(screen);
    }
  }
}
